<script lang="ts" setup>
import { Checkbox, showLoadingToast, showSuccessToast } from "vant";
import { useGlobalUser } from "@/stores/globalUser";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useCoin } from "@/stores/coin";
import { useRouter } from "vue-router";
import { countWords } from "@homegrown/word-counter";
import {
  VoteTargetEntityType,
  VoteTargetType,
} from "@/composable/rank/rankConfig";
import { CUBE_NUM_PER_TICKET } from "@/modules/constant";

const props = defineProps<{
  type: VoteTargetEntityType;
  item: VoteTargetType;
  loading: boolean;
}>();
const emit = defineEmits<{
  (e: "onVoted"): void;
}>();

// Constants & Stores
const CoinsPerTicket = 2;
const user = useGlobalUser();
const router = useRouter();
const coinStore = useCoin();
const { t } = useI18n();
// States
const ticketCount = ref(1);
const voteMessage = ref("");
const isAnonymous = ref(false);
const toConsumptionCoins = computed(() => ticketCount.value * CoinsPerTicket);
const restCoins = computed(() => user.coinAmount - toConsumptionCoins.value);
const voteErrorMsg = computed(() => {
  if (restCoins.value < 0) {
    return t("Coin.noEnoughCoin");
  }
  if (coinStore.restExchangeableTicketToday - ticketCount.value < 0) {
    return t("Coin.overTodayRestVotingCount");
  }
  return "";
});
const wordsCount = computed(() => countWords(voteMessage.value));
const isVoteMessageValid = computed(() => {
  return wordsCount.value >= 10;
});

const isValidToVote = computed(() => {
  if (
    props.loading ||
    !ticketCount.value ||
    restCoins.value < 0 ||
    coinStore.restExchangeableTicketToday - ticketCount.value < 0
  )
    return false;
  if (isAnonymous.value) {
    return isVoteMessageValid.value;
  } else {
    return true;
  }
});

const onVote = async () => {
  const loading = showLoadingToast("");
  try {
    await coinStore.vote({
      entityType: props.type,
      entityId: props.item.id!,
      amount: Number(ticketCount.value),
      text: voteMessage.value,
      isAnonymous: isAnonymous.value ? 1 : 0,
    });
    emit("onVoted");
    loading.close();
    showSuccessToast(t("Coin.voted"));
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="flex flex-col gap-2 flex-1">
    <van-field
      v-model="ticketCount"
      :error-message="voteErrorMsg"
      :label="$t('Coin.ticketNum')"
      :placeholder="$t('Coin.inputTicketNum')"
      class="bg-base-100 border rounded"
      type="digit"
    >
      <template #left-icon>
        <div class="h-full flex items-center">
          <i class="iconify mdi--ticket text-xs" />
        </div>
      </template>
    </van-field>
    <div class="flex flex-col gap-1">
      <div class="relative">
        <van-field
          v-model="voteMessage"
          :placeholder="$t('Coin.leaveMessage')"
          :rows="5"
          class="bg-base-100 border rounded"
          label-align="top"
          show-word-limit
          type="textarea"
        />
        <div
          v-if="isAnonymous"
          class="absolute right-0 bottom-0 p-1 text-base-content/50"
        >
          {{ wordsCount }}
        </div>
      </div>
      <span v-if="isAnonymous && !isVoteMessageValid" class="text-warning">
        {{ $t("Coin.anonymousVoteLimit") }}
      </span>
      <div class="flex justify-between items-center">
        <span class="flex items-center gap-1 text-base-content/50 text-xs">
          <span>{{ $t("Coin.restCoins") }}:</span>
          <span v-if="loading" class="loading loading-xs" />
          <span v-else>{{ user.coinAmount }}</span>
          <i class="iconfont icon-coin text-xs" />
        </span>
        <button
          class="btn btn-xs rounded btn-neutral"
          @click="router.push({ name: 'coinTask' })"
        >
          {{ $t("Coin.getCoin") }}
        </button>
      </div>
      <div class="text-xs text-base-content/50">
        <span>{{ $t("Coin.todayRestVotingCount") }}:</span>
        <span v-if="loading" class="loading loading-xs" />
        <span v-else>{{ coinStore.restExchangeableTicketToday }}</span>
      </div>
    </div>
  </div>
  <div class="flex flex-col gap-3">
    <div
      class="flex flex-col items-end text-base-content/50 gap-1 text-xs md:text-sm"
    >
      <span class="flex items-center gap-1">
        {{ $t("Coin.consumptionCoins") }}:
        <span class="text-primary">{{ toConsumptionCoins }}</span>
        <i class="iconfont icon-coin text-xs" />
      </span>
      <span class="flex items-center gap-1">
        <i class="iconify mdi--ticket text-xs" />
        <span>{{ $t("Coin.ticketNum") }}:</span>
        <span class="text-base-content">{{ ticketCount }}</span>
      </span>
      <span class="flex items-center gap-1">
        <span>
          {{ $t(`Coin.give`) }} {{ ticketCount * CUBE_NUM_PER_TICKET }}
        </span>
        <PowerIcon />
      </span>
    </div>
    <div class="flex items-center justify-between gap-4">
      <div></div>
      <div class="flex items-center justify-end gap-4">
        <span class="flex items-center gap-1">
          <Checkbox v-model="isAnonymous" icon-size="15" shape="square" />
          <span class="text-base-content/50">{{
            $t("Coin.anonymousVote")
          }}</span>
        </span>
        <button
          :disabled="!isValidToVote"
          class="btn btn-sm rounded btn-primary"
          @click="onVote"
        >
          {{ $t("Coin.buyTicketsAndVote") }}
        </button>
      </div>
    </div>
  </div>
</template>
